// @ts-nocheck -- legacy typings pending cleanup; runtime behavior unchanged
import { useEffect, useMemo, useState } from "react";
import {
  buildAdvancedCurve,
  type AdvancedCurveSeries,
  type AdvancedDoseInput,
} from "@/domain/advancedCurveEngine";
import { buildSessionContext } from "@/domain/sessionContextEngine";
import { getRuntimeKnowledgeBase } from "@/lib/pharmacology/runtimeKnowledge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PhaseBadge } from "@/components/clinical/PhaseBadge";
import {
  Activity,
  BrainCircuit,
  Layers3,
  Maximize2,
  Minimize2,
  MousePointer2,
  SplitSquareVertical,
  TriangleAlert,
} from "lucide-react";
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const windows = [12, 24, 48, 72, 168];

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload ?? {};
  const visible = payload.filter((item: any) => !String(item.dataKey).includes("__"));
  return (
    <div className="min-w-[240px] rounded-lg border bg-card p-3 text-xs shadow-lg">
      <div className="mb-1 font-medium">{label}</div>
      <div className="mb-1 flex justify-between gap-4 border-b pb-1">
        <span className="text-muted-foreground">Carga selecionada</span>
        <span className="font-mono font-semibold">
          {Number(row.selectedTotal ?? row.total ?? 0).toFixed(0)}/100
        </span>
      </div>
      <div className="space-y-1">
        {visible.map((item: any) => {
          if (item.dataKey === "selectedTotal" || item.dataKey === "total") return null;
          const phase = row[`${item.dataKey}__phase`] ?? "—";
          return (
            <div key={item.dataKey} className="grid grid-cols-[10px_1fr_auto] items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: item.color }} />
              <span className="truncate">{item.name}</span>
              <span className="font-mono">{Number(item.value ?? 0).toFixed(0)}/100 · {phase}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-[10px] text-muted-foreground">
        Curva relativa de efeito modelado; não é concentração sérica.
      </div>
    </div>
  );
}

function phaseText(phase: string) {
  const map: Record<string, string> = {
    "pre-onset": "pré-onset",
    subida: "subida",
    pico: "pico",
    platô: "platô",
    descida: "descida/offset",
    residual: "cauda residual",
    washout: "washout",
    "steady-state": "steady-state",
  };
  return map[phase] ?? phase;
}

function ContextPanel({
  doses,
  bodyWeightKg,
  now,
}: {
  doses: AdvancedDoseInput[];
  bodyWeightKg?: number | null;
  now: Date;
}) {
  const knowledge = useMemo(() => getRuntimeKnowledgeBase(), []);
  const nowMs = now.getTime();
  const snapshot = useMemo(
    () =>
      buildSessionContext({
        allDoses: doses as any[],
        at: now,
        knowledge,
        bodyWeightKg: bodyWeightKg ?? undefined,
      }),
    [doses, nowMs, knowledge, bodyWeightKg],
  );

  const projectedAxes = useMemo(() => {
    const benefits = new Map<string, number>();
    const adverse = new Map<string, number>();
    for (const state of snapshot.states) {
      const substance = state.latestDose?.substances ?? {};
      const factor = Math.max(0, Math.min(1, state.effect0to100 / 100));
      for (const [axis, raw] of Object.entries(substance.clinical_effect_profile ?? {})) {
        const value = Number(raw);
        if (!Number.isFinite(value)) continue;
        benefits.set(axis, Math.min(100, (benefits.get(axis) ?? 0) + value * factor));
      }
      for (const [axis, raw] of Object.entries(substance.adverse_effect_profile ?? {})) {
        const value = Number(raw);
        if (!Number.isFinite(value)) continue;
        adverse.set(axis, Math.min(100, (adverse.get(axis) ?? 0) + value * factor));
      }
    }
    const rank = (map: Map<string, number>) =>
      [...map.entries()]
        .map(([axis, score]) => ({ axis, score: Math.round(score) }))
        .filter((item) => item.score >= 8)
        .sort((a, b) => b.score - a.score)
        .slice(0, 8);
    return { benefits: rank(benefits), adverse: rank(adverse) };
  }, [snapshot.states]);

  if (!snapshot.states.length) {
    return (
      <div className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
        Nenhuma substância com atividade/carry-over suficiente para gerar leitura contextual agora.
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-2xl border border-primary/20 bg-primary/5 p-3 sm:p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 font-medium">
            <BrainCircuit className="h-4 w-4 text-primary" />
            Leitura provável/possível agora
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Integra fase modelada, carry-over/cauda, carga PK estimada, sobreposição e padrão longitudinal de tolerância.
            É hipótese contextual, não confirmação do que a pessoa está sentindo.
          </p>
        </div>
        <Badge variant="outline">{snapshot.states.length} ativa(s)/residual(is)</Badge>
      </div>

      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {snapshot.states.slice(0, 9).map((state) => (
          <div key={state.key} className="rounded-xl border bg-background/80 p-3 text-xs">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 font-medium">{state.name}</div>
              <PhaseBadge phase={state.phase as any} />
            </div>
            <div className="mt-2 space-y-1 text-muted-foreground">
              <div>
                Efeito relativo agora: <span className="font-mono text-foreground">{state.effect0to100.toFixed(0)}/100</span>
              </div>
              {state.body ? (
                <div>
                  Carga PK residual: <span className="font-mono text-foreground">{(state.body.remainingFraction * 100).toFixed(0)}%</span>
                </div>
              ) : null}
              <div>
                Tolerância/adaptação: <span className="text-foreground">{state.tolerance.label}</span>
              </div>
              {state.carryOver ? <div>Origem: <span className="text-foreground">carry-over de dose anterior</span></div> : null}
            </div>
            <details className="mt-2">
              <summary className="cursor-pointer text-[10px] text-muted-foreground">Por que o sistema classificou assim</summary>
              <div className="mt-1 text-[10px] leading-relaxed text-muted-foreground">{state.tolerance.reason}</div>
            </details>
          </div>
        ))}
      </div>

      {projectedAxes.benefits.length || projectedAxes.adverse.length ? (
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Efeitos subjetivos/terapêuticos mais plausíveis</div>
            <div className="flex flex-wrap gap-1.5">
              {projectedAxes.benefits.length
                ? projectedAxes.benefits.map((item) => (
                    <Badge key={item.axis} variant="secondary" className="gap-1">
                      {item.axis} <span className="font-mono">{item.score}/100</span>
                    </Badge>
                  ))
                : <span className="text-xs text-muted-foreground">Sem perfil clínico estruturado suficiente.</span>}
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Efeitos adversos mais plausíveis</div>
            <div className="flex flex-wrap gap-1.5">
              {projectedAxes.adverse.length
                ? projectedAxes.adverse.map((item) => (
                    <Badge key={item.axis} variant="outline" className="gap-1">
                      {item.axis} <span className="font-mono">{item.score}/100</span>
                    </Badge>
                  ))
                : <span className="text-xs text-muted-foreground">Sem perfil adverso estruturado suficiente.</span>}
            </div>
          </div>
          <p className="text-[10px] leading-relaxed text-muted-foreground lg:col-span-2">
            Os escores acima ponderam o perfil cadastrado de cada substância pela intensidade relativa modelada no momento.
            Tolerância, sono, contexto e resposta individual podem deslocar muito a experiência; por isso a inferência de tolerância é exibida separadamente e não vira um “desconto” numérico arbitrário.
          </p>
        </div>
      ) : null}

      {snapshot.effects.length ? (
        <div className="space-y-2">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Fenômenos plausíveis no momento</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {snapshot.effects.slice(0, 8).map((effect) => (
              <div key={effect.id} className="rounded-lg border bg-background/70 p-2.5 text-xs">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <strong>{effect.label}</strong>
                  <Badge variant={effect.status === "sinal de alarme" ? "destructive" : "outline"}>{effect.status}</Badge>
                </div>
                <p className="mt-1 leading-relaxed text-muted-foreground">{effect.rationale}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {snapshot.interactions.length ? (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <TriangleAlert className="h-3.5 w-3.5" />
            Interações contextuais ativas/residuais
          </div>
          <div className="space-y-2">
            {snapshot.interactions.slice(0, 5).map((interaction) => (
              <details key={interaction.id} className="rounded-lg border bg-background/70 p-2.5 text-xs">
                <summary className="cursor-pointer font-medium">
                  {interaction.title} · {interaction.level}
                </summary>
                <div className="mt-2 space-y-1 text-muted-foreground">
                  <p>{interaction.rationale}</p>
                  <p><b className="text-foreground">Contexto:</b> {interaction.context}</p>
                  <p><b className="text-foreground">Monitorar:</b> {interaction.monitor.join(", ")}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      ) : null}

      <p className="text-[10px] leading-relaxed text-muted-foreground">
        Ocultar uma substância no gráfico muda apenas a visualização. A síntese clínica acima continua considerando todas as ingestões registradas que ainda estejam ativas ou em cauda/carry-over.
      </p>
    </div>
  );
}

function SeparateChart({
  series,
  points,
  nowLabel,
}: {
  series: AdvancedCurveSeries;
  points: Record<string, any>[];
  nowLabel?: string;
}) {
  return (
    <div className="space-y-2 rounded-xl border bg-card p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2 text-sm font-medium">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: series.color }} />
          <span className="truncate">{series.label}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="font-mono">{series.nowValue.toFixed(0)}/100</span>
          <PhaseBadge phase={series.nowPhase as any} />
        </div>
      </div>
      <div className="h-48 min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ top: 8, right: 14, bottom: 4, left: -12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={9} minTickGap={34} />
            <YAxis domain={[0, 100]} stroke="hsl(var(--muted-foreground))" fontSize={9} width={30} />
            <Tooltip content={<CustomTooltip />} />
            {nowLabel ? (
              <ReferenceLine
                x={nowLabel}
                stroke="hsl(var(--foreground))"
                strokeWidth={2}
                strokeDasharray="4 3"
                label={{ value: "agora", position: "insideTopRight", fill: "hsl(var(--foreground))", fontSize: 9 }}
              />
            ) : null}
            <Line
              type="monotone"
              dataKey={series.key}
              name={series.label}
              stroke={series.color}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10px] text-muted-foreground space-y-0.5">
        <div>Fase atual: <b className="text-foreground">{phaseText(series.nowPhase)}</b>{series.hoursSinceLastDose != null ? ` · última dose há ${series.hoursSinceLastDose.toFixed(1)} h` : ""}</div>
        {series.nextPeakAt ? <div>Próximo máximo modelado: <b className="text-foreground">{new Date(series.nextPeakAt).toLocaleString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</b> · {series.nextPeakValue?.toFixed(0)}/100</div> : null}
        {series.notes[0] ? <div>{series.notes[0]}</div> : null}
      </div>
    </div>
  );
}

function ChartBlock({
  doses,
  bodyWeightKg,
  height = 360,
  expanded = false,
}: {
  doses: AdvancedDoseInput[];
  bodyWeightKg?: number | null;
  height?: number;
  expanded?: boolean;
}) {
  const [windowHours, setWindowHours] = useState(48);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [mode, setMode] = useState<"integrated" | "separate">("integrated");
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const now = useMemo(() => new Date(nowMs), [nowMs]);
  const result = useMemo(
    () => buildAdvancedCurve(doses, { windowHours, stepMin: windowHours > 72 ? 30 : 15, now }),
    [doses, windowHours, nowMs],
  );
  const visible = (series: AdvancedCurveSeries) => enabled[series.key] !== false;
  const visibleSeries = useMemo(
    () => result.series.filter((series) => enabled[series.key] !== false),
    [result.series, enabled],
  );
  const toggle = (key: string) => setEnabled((current) => ({ ...current, [key]: current[key] === false ? true : false }));
  const showOnly = (key: string) =>
    setEnabled(Object.fromEntries(result.series.map((series) => [series.key, series.key === key])));
  const showAll = () => setEnabled({});
  const showNone = () => setEnabled(Object.fromEntries(result.series.map((series) => [series.key, false])));

  const chartData = useMemo(
    () =>
      result.points.map((row) => ({
        ...row,
        selectedTotal: Math.round(
          Math.min(
            100,
            visibleSeries.reduce((sum, series) => sum + Number(row[series.key] ?? 0), 0),
          ) * 10,
        ) / 10,
      })),
    [result.points, visibleSeries],
  );

  const nowPoint = useMemo(() => {
    if (!chartData.length) return null;
    return chartData.reduce((best, row) =>
      Math.abs(Number(row.timestamp) - nowMs) < Math.abs(Number(best.timestamp) - nowMs) ? row : best,
    chartData[0]);
  }, [chartData, nowMs]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="gap-1">
          <Activity className="h-3 w-3" />
          Todas as substâncias agora {result.now.total.toFixed(0)}/100 · {phaseText(result.now.phase)}
        </Badge>
        <Badge variant="secondary">
          selecionadas {visibleSeries.length}/{result.series.length}
        </Badge>
        <select
          className="h-8 rounded-md border border-input bg-background px-2 text-xs"
          value={windowHours}
          onChange={(event) => setWindowHours(Number(event.target.value))}
        >
          {windows.map((windowValue) => (
            <option key={windowValue} value={windowValue}>
              {windowValue <= 72 ? `${windowValue} h` : "7 dias"}
            </option>
          ))}
        </select>
        <div className="flex rounded-lg border p-0.5">
          <Button
            type="button"
            variant={mode === "integrated" ? "secondary" : "ghost"}
            size="sm"
            className="h-7"
            onClick={() => setMode("integrated")}
          >
            <Layers3 className="mr-1 h-3.5 w-3.5" />Integrado
          </Button>
          <Button
            type="button"
            variant={mode === "separate" ? "secondary" : "ghost"}
            size="sm"
            className="h-7"
            onClick={() => setMode("separate")}
          >
            <SplitSquareVertical className="mr-1 h-3.5 w-3.5" />Separados
          </Button>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={showAll}>Todas</Button>
        <Button type="button" variant="ghost" size="sm" onClick={showNone}>Nenhuma</Button>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {result.series.map((series) => (
          <div
            key={series.key}
            className={`flex min-w-0 items-center gap-2 rounded-xl border p-2 ${visible(series) ? "bg-background" : "bg-muted/40 text-muted-foreground"}`}
          >
            <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={visible(series)}
                onChange={() => toggle(series.key)}
                className="h-4 w-4 shrink-0 accent-primary"
              />
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: series.color }} />
              <span className="min-w-0 flex-1 truncate text-xs font-medium">{series.label}</span>
            </label>
            <span className="shrink-0 font-mono text-[10px]">{series.nowValue.toFixed(0)}/100</span>
            <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={() => showOnly(series.key)}>
              Só esta
            </Button>
          </div>
        ))}
      </div>

      {result.notes.length ? (
        <div className="space-y-1 rounded-xl border border-warning/30 bg-warning/5 p-2.5 text-xs">
          {result.notes.map((note) => <div key={note}>{note}</div>)}
        </div>
      ) : null}

      {mode === "integrated" ? (
        visibleSeries.length ? (
          <div className="overflow-x-auto rounded-xl border bg-card">
            <div style={{ minWidth: expanded ? 1400 : 920, height }} className="p-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 24, bottom: 10, left: 0 }}
                  onClick={(state: any) => state?.activePayload?.[0]?.payload && setSelectedRow(state.activePayload[0].payload)}
                  onMouseMove={(state: any) => state?.activePayload?.[0]?.payload && setSelectedRow(state.activePayload[0].payload)}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} minTickGap={28} />
                  <YAxis domain={[0, 100]} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={50} stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  {nowPoint ? (
                    <ReferenceLine
                      x={nowPoint.label}
                      stroke="hsl(var(--foreground))"
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      label={{ value: "AGORA", position: "insideTopRight", fill: "hsl(var(--foreground))", fontSize: 10 }}
                    />
                  ) : null}
                  <Line
                    type="monotone"
                    dataKey="selectedTotal"
                    name="Carga selecionada"
                    stroke="hsl(var(--foreground))"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  {visibleSeries.map((series) => (
                    <Line
                      key={series.key}
                      type="monotone"
                      dataKey={series.key}
                      name={series.label}
                      stroke={series.color}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                  <Brush dataKey="label" height={28} travellerWidth={14} stroke="hsl(var(--primary))" />
                  <Legend verticalAlign="top" height={28} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
            Nenhuma substância selecionada. Marque uma ou mais acima.
          </div>
        )
      ) : (
        <div className="grid gap-3 xl:grid-cols-2">
          {visibleSeries.length ? (
            visibleSeries.map((series) => (
              <SeparateChart key={series.key} series={series} points={result.points} nowLabel={nowPoint?.label} />
            ))
          ) : (
            <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
              Marque ao menos uma substância para gerar os gráficos separados.
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <MousePointer2 className="h-3.5 w-3.5" />
        A linha vertical “AGORA” atualiza automaticamente. Toque/passe o cursor para fixar um horário específico.
      </div>

      {selectedRow ? (
        <div className="space-y-2 rounded-xl border bg-muted/30 p-3 text-xs">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-medium">Leitura fixa: {selectedRow.label}</div>
            <Badge variant="outline">Selecionadas {Number(selectedRow.selectedTotal ?? 0).toFixed(0)}/100</Badge>
          </div>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {visibleSeries.map((series) => (
              <div key={series.key} className="rounded-md border bg-background p-2">
                <div className="flex items-center gap-1 font-medium">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: series.color }} />
                  {series.label}
                </div>
                <div className="text-muted-foreground">
                  Intensidade: <span className="font-mono text-foreground">{Number(selectedRow[series.key] ?? 0).toFixed(0)}/100</span>
                </div>
                <div className="text-muted-foreground">
                  Fase: <span className="text-foreground">{phaseText(selectedRow[`${series.key}__phase`] ?? "—")}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <ContextPanel doses={doses} bodyWeightKg={bodyWeightKg} now={now} />
    </div>
  );
}

export function AdvancedCurveExplorer({
  doses,
  title = "Curva PK/PD relativa",
  description,
  bodyWeightKg,
}: {
  doses: AdvancedDoseInput[];
  title?: string;
  description?: string;
  bodyWeightKg?: number | null;
}) {
  const [open, setOpen] = useState(false);

  if (!doses?.length) {
    return (
      <Card className="p-4 text-sm text-muted-foreground">
        Sem doses para construir curva. Registre dose com horário real e, idealmente, formulação.
      </Card>
    );
  }

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-xs text-muted-foreground">
            {description ?? "Intensidade relativa 0–100. Selecione substâncias, compare gráficos separados e use a linha AGORA para localizar a fase atual."}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          <Maximize2 className="mr-1 h-4 w-4" />Expandir
        </Button>
      </div>

      <ChartBlock doses={doses} bodyWeightKg={bodyWeightKg} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[94vh] max-w-[96vw] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Minimize2 className="h-4 w-4" />Curvas farmacológicas expandidas
            </DialogTitle>
          </DialogHeader>
          <ChartBlock doses={doses} bodyWeightKg={bodyWeightKg} height={560} expanded />
        </DialogContent>
      </Dialog>
    </Card>
  );
}
